<template>
  <div
    v-if="product"
    class="product-content-details"
  >
    <Heading
      v-if="headline"
      tag="h3"
      size="md"
      class="product-content-details__heading"
      :text="headline"
      spaces-bottom
    />
    <template
      v-for="(childProduct, key) in getProducts"
      :key="key"
    >
      <div
        v-if="shouldRenderEntry(childProduct)"
        ref="sections"
        class="product-content-details__section"
      >
        <Heading
          v-if="product.bundle && childProduct?.family?.name"
          :tag="headline ? 'h4' : 'h3'"
          size="sm"
          class="product-content-details__product-heading"
          spaces-bottom
          :text="bundledFamilyNameWithCount(childProduct.family.name)"
        />
        <slot
          :product="childProduct"
          :offer="offer"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { countBy, uniqBy } from 'lodash-es';
import { Heading } from '@i22team/td-longtail-vue-components';

export type ProductContentDetailsProps = {
  offer?: Offer;
  renderItemIf?: (p: OfferBundleProduct | OfferStandardProduct) => boolean;
  headline?: string;
};

const props = withDefaults(defineProps<ProductContentDetailsProps>(), {
  offer: undefined,
  renderItemIf: undefined,
  headline: undefined,
});

const product = computed(() => props.offer?.product || undefined);

const bundledProducts = computed<OfferBundleProduct[]>(() => {
  if (!product.value) return [];
  const bundleProduct = getBundleProduct(product.value);
  if (bundleProduct) {
    return bundleProduct.bundledProducts || [];
  }
  return [];
});

const groupedBundledProducts = computed(() =>
  uniqBy(bundledProducts.value, (p: OfferBundleProduct) => p?.family?.name)
);
const bundledProductFamilyCounts = computed(() =>
  countBy(bundledProducts.value, (p) => p?.family?.name)
);
const getProducts = computed<(OfferBundleProduct | OfferStandardProduct)[]>(() => {
  if (!product.value) return [];
  return groupedBundledProducts.value.length > 0
    ? groupedBundledProducts.value
    : [getStandardProduct(product.value)];
});

const bundledFamilyNameWithCount = (familyName: string) => {
  const count = bundledProductFamilyCounts.value?.[familyName] || 0;
  if (count <= 1) return familyName;
  return `${count} × ${familyName}`;
};
const shouldRenderEntry = (p: OfferBundleProduct | OfferStandardProduct) => {
  if (props.renderItemIf) {
    return !!props.renderItemIf(p);
  }
  return true;
};
defineExpose({ groupedBundledProducts, bundledProductFamilyCounts, bundledFamilyNameWithCount });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.product-content-details {
  &__heading {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }

  &__product-heading {
    @include mq(false, $mq-md - 1) {
      font-size: 24px;
    }
  }

  &__section {
    margin-bottom: $grid-base;
    border-bottom: 1px solid var(--color-gray-100);
    padding-bottom: $grid-base;
  }
}
</style>
